import type { Ref } from 'vue'
import type { Router, RouteRecordRaw } from 'vue-router'
import type { NavigationDrawerSection } from '@/apps/core'
import type { MembershipSummary } from '@/api/session/access'
import type { OrgPermission } from '@/types/permissions'

export type RouteLocation = string | { name: string; params?: Record<string, any>; query?: Record<string, any> }

export interface SettingsFeatures {
  apiTokens?: boolean
  profile?: boolean
  dangerZone?: boolean
  organizationMembers?: boolean
  organizationRoles?: boolean
}

export interface SettingsRedirects {
  afterTokenCreate?: RouteLocation
  afterAccountDelete?: RouteLocation
  backRoute?: RouteLocation
}

// `apiTokenRequest` and `legalIdentityRequest` were here until 2026-09-19. Both named
// `helpdesk:new-ticket`, and support ticketing is abandoned (owner's decision, that day), so
// they named a route no application registers. The screens that read them say what they say
// now: the change goes through the team, with no in-product form to open.

/**
 * The project's own drawer sections. Called once, in the settings layout's setup — so it may hold a
 * query of its own — with the organization as refs; the sections it returns are recomputed as what
 * they read changes.
 */
export type CustomNavigationSectionsProvider = (context: {
  organizationId: Readonly<Ref<string | undefined>>
  /** The caller's membership of that organization. */
  organization: Readonly<Ref<MembershipSummary | undefined>>
  t: (key: string) => string
  can: (permission: OrgPermission) => boolean
}) => Readonly<Ref<NavigationDrawerSection[]>>

export interface SettingsPluginOptions {
  router: Router
  features?: SettingsFeatures
  redirects?: SettingsRedirects
  basePath?: string
  /** Max width of the settings content column (any CSS length). Defaults to '900px'. */
  contentMaxWidth?: string
  customRoutes?: RouteRecordRaw[]
  customNavigationSections?: CustomNavigationSectionsProvider
  parentRouteName?: string
}

export interface SettingsConfig extends Required<Omit<SettingsPluginOptions, 'features' | 'redirects' | 'customRoutes' | 'customNavigationSections' | 'parentRouteName'>> {
  features: Required<SettingsFeatures>
  redirects: Required<SettingsRedirects>
  customRoutes?: RouteRecordRaw[]
  customNavigationSections?: CustomNavigationSectionsProvider
  parentRouteName?: string
}

export const defaultSettingsConfig: Omit<SettingsConfig, 'router'> = {
  basePath: '/settings',
  contentMaxWidth: '900px',
  features: {
    apiTokens: true,
    profile: true,
    dangerZone: true,
    organizationMembers: true,
    organizationRoles: true,
  },
  redirects: {
    afterTokenCreate: { name: 'settings:api-tokens' },
    afterAccountDelete: { name: 'session:login' },
    backRoute: { name: 'home' },
  },
}

export function mergeSettingsConfig(
  router: Router,
  options: Partial<SettingsPluginOptions> = {}
): SettingsConfig {
  return {
    router,
    basePath: options.basePath ?? defaultSettingsConfig.basePath,
    contentMaxWidth: options.contentMaxWidth ?? defaultSettingsConfig.contentMaxWidth,
    features: {
      ...defaultSettingsConfig.features,
      ...options.features,
    },
    redirects: {
      ...defaultSettingsConfig.redirects,
      ...options.redirects,
    },
    customRoutes: options.customRoutes,
    customNavigationSections: options.customNavigationSections,
    parentRouteName: options.parentRouteName,
  }
}
